import { nepaliFiscalYearWindow, todayAd } from '../date/bikramSambat';
import type { BillData } from '../schema/billData';
import type { FieldValidationError } from '../schema/validationError';

/**
 * Deterministic, non-AI business rules verified against the real IRD
 * Enrollment form (docs/ird-site-analysis.md). Pure functions, shared
 * byte-for-byte between client-side live validation (Review screen) and the
 * function's final server-side gate — the one place these rules live, so the
 * two can never drift apart.
 */

export function validateBillNumber(billNumber: string | null): FieldValidationError | null {
  if (!billNumber || billNumber.trim().length === 0) {
    return { field: 'billNumber', code: 'required', message: 'Bill number is required.' };
  }
  return null;
}

const PAN_RE = /^\d{9}$/;

export function validatePan(pan: string | null): FieldValidationError | null {
  if (!pan) return { field: 'sellerPan', code: 'required', message: 'Seller PAN/VAT number is required.' };
  if (!PAN_RE.test(pan)) {
    return { field: 'sellerPan', code: 'invalid_format', message: 'PAN/VAT number must be exactly 9 digits.' };
  }
  return null;
}

export function validateAmount(amount: number | null): FieldValidationError | null {
  if (amount === null || amount === undefined) {
    return { field: 'amount', code: 'required', message: 'Amount is required.' };
  }
  if (!Number.isFinite(amount) || amount <= 0) {
    return { field: 'amount', code: 'out_of_range', message: 'Amount must be greater than zero.' };
  }
  return null;
}

// Mobile number is a UserProfile field, not a BillData field — its
// structural rule (7-15 digits) is enforced by UserProfileSchema (zod)
// directly wherever a profile is saved, so it doesn't need a validator here.

/**
 * Checks the bill date (AD, "YYYY-MM-DD") isn't in the future and falls
 * within the currently active Nepali fiscal year — both verified rules on
 * the live IRD form. ISO date strings compare correctly as plain strings, so
 * no Date-object/timezone math is needed here.
 */
export function validateBillDate(billDateAd: string | null): FieldValidationError | null {
  if (!billDateAd) return { field: 'billDateAd', code: 'required', message: 'Bill date is required.' };

  const today = todayAd();
  if (billDateAd > today) {
    return { field: 'billDateAd', code: 'future_date', message: 'Bill date cannot be in the future.' };
  }

  const window = nepaliFiscalYearWindow(today);
  if (billDateAd < window.startAd || billDateAd > window.endAd) {
    return {
      field: 'billDateAd',
      code: 'outside_fiscal_year',
      message: 'Bill date must fall within the current fiscal year.',
    };
  }
  return null;
}

/**
 * Runs every business rule against a normalized BillData and returns all
 * violations (never throws) — the final deterministic gate before a bill is
 * considered ready for Review/IRD-assist. Gemini's confidence never overrides
 * this: a "high confidence" field that fails a business rule still fails.
 */
export function applyBusinessValidation(billData: BillData): FieldValidationError[] {
  return [
    validateBillNumber(billData.billNumber.value),
    validatePan(billData.sellerPan.value),
    validateAmount(billData.amount.value),
    validateBillDate(billData.billDateAd.value),
  ].filter((error): error is FieldValidationError => error !== null);
}
